import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from "@heroicons/react/outline";
import { AppTokenPayload, HubspotAppData, HubspotAppObject, Workflow, WorkflowAutomation } from "./interface";
import { useEffect, useMemo, useState } from "react";
import { BLOCK_ICON_MAP, EMAIL_REGEX } from "./utils";
import AppHubspotContactsInput from "./HubspotContactsInput";
import Breadcrumbs from "./Breadcrumbs";
import { useTranslation } from "react-i18next";
import { postData } from "./backend";
import { jwtDecode } from "jwt-decode";

interface Props {
  token: string;
  workflow: Workflow;
  appData: HubspotAppData | null;
  currentPage: "DATA" | "RECIPIENTS";
  onClickBack: () => void;
  onClickRecipeints: () => void;
  onStartAutomation: (automation: WorkflowAutomation) => void;
}

function AppWorkflowData(props: Props) {
  const { t } = useTranslation();

  const { token, appData } = props;

  const { workflow } = appData || props;
  const associatedObjects = props.appData?.associatedObjectOptions;

  const [filterValue, setFilterValue] = useState<string>("");

  const [selectedObjects, setSelectedObjects] = useState<Record<string, boolean>>();
  const [selectedContacts, setSelectedContacts] = useState<Record<string, string>>();

  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const currentDocument = workflow?.documents?.[currentDocumentIndex];

  const [hasSetAssumedOverrides, setHasSetAssumedOverrides] = useState(false);

  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<Error>();

  const contactOptions = useMemo(() => appData?.associatedObjectOptions.find((objList) => objList.toId === "0-1")?.items ?? [], [appData?.associatedObjectOptions]);
  const appTokenPayload = useMemo(() => {
    try {
      return jwtDecode<AppTokenPayload>(token);
    } catch {
      return undefined;
    }
  }, [token]);
  const userSuggestionsEnabled = !!appTokenPayload?.user_suggestions_enabled;

  const isRunningFromSecondary = appData?.primaryObjectType !== appData?.sourceObjectType;
  const allSectedContactsValid = Object.values(selectedContacts || {}).every((email) => (email || "").match(EMAIL_REGEX));

  const hasRecipients = !!workflow.signatureConfig;

  const numDocuments = workflow.documents?.length ?? 0;
  const isAtFirstDocument = currentDocumentIndex <= 0;
  const isAtLastDocument = currentDocumentIndex >= numDocuments - 1;

  useEffect(() => {
    if (!appData || !contactOptions || hasSetAssumedOverrides)
      return;

    const initialContacts: Record<string, string> = {};

    for (const index in appData.signatureRequestInitialRecipients) {
      const recipientNumber = parseInt(index) + 1;

      const initialValue = appData.signatureRequestInitialRecipients[index];
      initialContacts[recipientNumber] = initialValue;

      const hubspotContactTag = initialValue.match(/{{__hscontact_(.+?)(?::(.+))?}}/);
      if (!hubspotContactTag)
        continue;

      const filteredContact = contactOptions.filter((contact) => !!contact.email);
      if (filteredContact.length === 0)
        continue;

      initialContacts[recipientNumber] = filteredContact[0].email!;

      const labelDisplayName = hubspotContactTag[2];
      if (!labelDisplayName)
        continue;

      for (const contact of filteredContact) {
        const hasLabel = !!contact.associationLabels?.find((label) => label.split(":")[0] === labelDisplayName);

        if (hasLabel && contact.email) {
          initialContacts[recipientNumber] = contact.email;
          break;
        }
      }
    }

    setHasSetAssumedOverrides(true);
    setSelectedContacts(initialContacts);
  }, [appData, contactOptions, hasSetAssumedOverrides, setSelectedContacts]);

  useEffect(() => {
    if (!appData || selectedObjects !== undefined)
      return;

    setSelectedObjects(appData.associatedObjectOptions.reduce((acc: Record<string, boolean>, objList: any) => {
      objList.items.forEach((obj: any) => acc[`${objList.toId}.${obj.id}`] = true);
      return acc;
    }, {} as Record<string, boolean>));

  }, [appData, selectedObjects]);

  function handleToggleSelectedObject(id: string) {
    if (id === appData?.sourceObjectCompoundId)
      return;

    setSelectedObjects((prev) => {
      if (prev === undefined)
        return { [id]: true };

      const newValue = !prev[id];
      return { ...prev, [id]: newValue };
    });
  }

  function handleToggleAll(objectType: string, checked: boolean) {
    setSelectedObjects((prev) => {
      if (prev === undefined)
        prev = {};

      for (const id of Object.keys(prev)) {
        if (id.startsWith(objectType + "."))
          prev[id] = checked;
      }

      return { ...prev };
    });
  }

  function handleRemoveOthers(objectType: string) {
    handleToggleAll(objectType, false);

    const objList = associatedObjects!.find((objList) => objList.toId === objectType)!;
    const filteredObjects = objList.items.filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase()));

    for (const obj of filteredObjects)
      handleToggleSelectedObject(`${objList.toId}.${obj.id}`);
  }

  function isAllSelected(objectType: string): boolean {
    if (!associatedObjects)
      return false;

    return !!associatedObjects
      .find((objList) => objList.toId === objectType)?.items
      .every((obj) => selectedObjects?.[`${objectType}.${obj.id}`]);
  }

  function handleSetValue(recipientNumber: number, value: string) {
    setSelectedContacts(prev => ({ ...prev, [recipientNumber]: value }));
  }

  function handleStartAutomation() {
    setIsStarting(true);
    const associatedObjectIds = (() => {
      if (selectedObjects === undefined)
        return undefined;

      return Object.entries(selectedObjects)
        .filter(([_, value]) => value)
        .map(([key, _]) => key);
    })();

    let selectedContactIds: Record<string, string> = {};
    for (const [key, email] of Object.entries(selectedContacts || {})) {
      const contact = contactOptions.find(contact => contact.email === email);
      if (contact)
        selectedContactIds[key] = contact.id;
    }

    const automationOptions: Record<string, any> = {
      "aiEnrichment": userSuggestionsEnabled,
      "associatedObjectIds": associatedObjectIds,
      "selectedContacts": selectedContacts,
      "selectedContactIds": selectedContactIds,
      ...(isRunningFromSecondary ? {
        "mode": "ASSOCIATION_SOURCE_ID",
        "associatedSourceId": appData?.sourceObjectCompoundId,
        // source object MUST be included in the associated objects payload
        "associatedObjectIds": [...associatedObjectIds || [], appData?.sourceObjectCompoundId],
      } : {})
    };

    postData(`/hubspot/app/data/${workflow.id}/`, token, automationOptions)
      .then(data => props.onStartAutomation(data))
      .catch(setError)
      .finally(() => setIsStarting(false));
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <div className="gap-6 text-red font-semibold text-center">
          An error has occured
        </div>
        <div className="text-gray-600 text-center">
          {error.message}
        </div>
      </div>
    );
  }

  if (!appData) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='w-32 h-32' />
        <div className="text-gray-400 font-semibold text-center text-sm mt-2">{t("Loading workflow data")}</div>
      </div>
    );
  }

  const showSelectRecipientsCTA = hasRecipients && props.currentPage === "DATA";
  const ctaLabel = showSelectRecipientsCTA
    ? t("Select Recipients")
    : isStarting
      ? `${t("Starting")}...`
      : t("Generate Document");
  const ctaDisabled = showSelectRecipientsCTA ? false : (!allSectedContactsValid || isStarting);
  const ctaOnClick = showSelectRecipientsCTA ? props.onClickRecipeints : handleStartAutomation;

  const hasMicrosoftUrl = !!currentDocument?.microsoftDocumentConfig?.fileEmbedViewUrl;
  const hasGoogleUrl = !!currentDocument?.file?.url;

  return (
    <div className="flex h-full gap-3">
      <div className="flex-1 min-w-0 flex flex-col bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 h-9 shrink-0 border-b border-gray-200">
          {!!currentDocument &&
            <>
              <div className="flex items-center gap-2 min-w-0">
                <img className="h-[18px] w-[18px] rounded-sm shrink-0" src={BLOCK_ICON_MAP[currentDocument.documentType]} alt="File icon" />
                <div className="text-sm font-semibold text-gray-700 font-gilroy truncate" title={currentDocument?.file?.name ?? undefined}>
                  {currentDocument?.file?.name}
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-xs text-gray-500 font-gilroy tabular-nums">
                  {t("Document")} {currentDocumentIndex + 1} {t("of")} {numDocuments}
                </div>
                <div className="flex items-center gap-1">
                  <ChevronUpIcon
                    className={`w-4 h-4 ${isAtFirstDocument ? "text-gray-200 cursor-not-allowed" : "text-gray-400 hover:text-gray-600 cursor-pointer"}`}
                    onClick={isAtFirstDocument ? undefined : () => setCurrentDocumentIndex(i => Math.max(0, i - 1))}
                  />
                  <ChevronDownIcon
                    className={`w-4 h-4 ${isAtLastDocument ? "text-gray-200 cursor-not-allowed" : "text-gray-400 hover:text-gray-600 cursor-pointer"}`}
                    onClick={isAtLastDocument ? undefined : () => setCurrentDocumentIndex(i => Math.min(numDocuments - 1, i + 1))}
                  />
                </div>
              </div>
            </>
          }
        </div>

        <div className="flex-1 min-h-0 p-1.5">
          {hasMicrosoftUrl &&
            <iframe title={t("Microsoft Editor")} className="w-full h-full border border-gray-200 rounded overflow-hidden bg-white" src={currentDocument!.microsoftDocumentConfig!.fileEmbedViewUrl!} />
          }
          {!hasMicrosoftUrl && hasGoogleUrl &&
            <iframe title={t("Google Editor")} className="w-full h-full border border-gray-200 rounded overflow-hidden bg-white" src={`${currentDocument!.file!.url!.replace("/edit", "/preview")}?widget&rm=minimal`} />
          }
          {!hasMicrosoftUrl && !hasGoogleUrl &&
            <div className="w-full h-full flex items-center justify-center bg-white border border-gray-200 rounded text-sm font-semibold text-gray-400">
              {t("Unable to load document preview")}
            </div>
          }
        </div>
      </div>

      <div className="w-[360px] shrink-0 relative flex flex-col h-full pl-4">
        <div className="flex flex-col pt-2 pb-6 shrink-0 border-b border-gray-200">
          <Breadcrumbs workflow={workflow} currentPage={props.currentPage} />
          <div className="mt-4 text-2xl font-gilroy font-semibold text-black leading-tight break-words text-balance">
            {workflow.name}
          </div>
        </div>

        <div className="relative z-0 flex-1 overflow-y-auto pt-4 pb-24 flex flex-col gap-3 pr-1">
          <div className="text-xs text-gray-500 font-semibold font-gilroy">
            {props.currentPage === "DATA"
              ? t("Select the associated records to be included:")
              : t("Select the intended recipients for this signable document")
            }
          </div>

          {props.currentPage === "RECIPIENTS" &&
            <div className="flex flex-col gap-4">
              {Array.from({ length: appData.signatureRequestNumRecipients || 0 }).map((_, index) =>
                <AppHubspotContactsInput key={index} index={index} value={selectedContacts![index + 1]} setValue={handleSetValue} appData={appData} />
              )}
            </div>
          }

          {props.currentPage === "DATA" &&
            <>
              {(associatedObjects?.flatMap((objLit) => objLit.items)?.length ?? 0) > 20 &&
                <div className="relative">
                  <SearchIcon className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input
                    className="w-full border border-gray-200 rounded-md pl-8 pr-2 text-sm py-1.5 font-gilroy outline-none focus:border-blue"
                    type="text"
                    placeholder={t("Search")}
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.currentTarget.value)}
                  />
                </div>
              }

              {associatedObjects?.length === 0 &&
                <div className="text-gray-400 font-semibold text-sm">{t("No associated objects to select from, the automation can be started as is.")}</div>
              }

              <div className="flex flex-col gap-4">
                {associatedObjects?.map((objList) => (
                  <div key={objList.toId} className="flex flex-col">
                    <div className="flex items-center gap-2 py-1 cursor-default">
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        checked={isAllSelected(objList.toId)}
                        disabled={(filterValue.length > 0) || appData.sourceObjectType === objList.toId}
                        onChange={() => handleToggleAll(objList.toId, !isAllSelected(objList.toId))}
                      />
                      <div className="text-sm font-semibold text-black font-gilroy">{objList.title}</div>
                      <div className="text-xs text-gray-400 font-semibold font-gilroy ml-auto">
                        {Object.entries(selectedObjects ?? {}).filter(([k, v]) => v && k.startsWith(objList.toId)).length} / {objList.items.length}
                      </div>
                    </div>

                    {objList.items.filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())).length === 0 &&
                      <div className="text-xs font-semibold text-gray-400 text-center mt-1">
                        {t("No")} {objList.title} {t("match this search")}
                      </div>
                    }

                    <div className="flex flex-col">
                      {objList.items
                        .filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase()))
                        .sort((a, b) => a.label?.localeCompare(b.label))
                        .map((obj: HubspotAppObject) => (
                          <div
                            key={obj.id}
                            className="flex items-center gap-2 py-1 px-1 -mx-1 rounded hover:bg-blue-50 cursor-pointer"
                            onClick={() => handleToggleSelectedObject(`${objList.toId}.${obj.id}`)}
                          >
                            <input
                              type="checkbox"
                              className="cursor-pointer"
                              checked={!!(selectedObjects?.[`${objList.toId}.${obj.id}`])}
                              onChange={(e) => { handleToggleSelectedObject(`${objList.toId}.${obj.id}`); e.stopPropagation(); }}
                              onClick={(e) => { e.stopPropagation(); }}
                              disabled={`${objList.toId}.${obj.id}` === appData.sourceObjectCompoundId}
                            />
                            <div className="flex items-center justify-between gap-2 min-w-0 w-full">
                              <div className="text-sm text-gray-600 font-semibold font-gilroy truncate">{obj.label}</div>
                              <div className="text-xs text-gray-400 font-semibold font-gilroy shrink-0">{obj.email || obj.id}</div>
                            </div>
                          </div>
                        ))}
                    </div>

                    {isAllSelected(objList.toId) && filterValue.length > 0 && objList.items.length > 1 && objList.items.filter(a => a.label?.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())).length > 0 &&
                      <div className="text-xs font-semibold text-gray-400 hover:text-blue cursor-pointer underline text-center mt-2" onClick={() => handleRemoveOthers(objList.toId)}>
                        {t("Unselect all others")}
                      </div>
                    }
                  </div>
                ))}
              </div>
            </>
          }
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10 pl-4 pt-3 pb-3 bg-white border-t border-gray-200">
          <div className="absolute -top-6 left-0 right-0 z-10 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none" />
          <div className="flex items-center gap-3">
            <button
              className="flex-1 h-10 bg-white hover:bg-gray-50 text-gray-700 font-gilroy font-semibold text-sm rounded-md border border-gray-200 transition-colors duration-150"
              onClick={props.onClickBack}
            >
              {t("Back")}
            </button>
            <button
              className="flex-1 h-10 bg-blue hover:bg-blue-10 text-white font-gilroy font-semibold text-sm rounded-md shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={ctaOnClick}
              disabled={ctaDisabled}
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppWorkflowData;
